import { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { Link, useNavigate } from 'react-router-dom';
import { CheckOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Input, Layout, Space, Tag } from 'antd';
import Table, { ColumnsType } from 'antd/es/table';

import { Role } from '@app/domain-interfaces';
import {
  AdvancedTechnologyEdaVendorDetail,
  AdvancedTechnologyEdaVendorProcessDetail,
} from '@app/domain-maps-interfaces';
import { Auth, ConfirmDelete, FeatureFlags, PageLayout, Popover, Tables, Theme } from '@app/web-components';

import {
  AuditFilters,
  CreatedByColumn,
  CreatedOnColumn,
  UpdatedByColumn,
  UpdatedOnColumn,
} from '../../../../../common';
import { useDeleteEdaVendor, useEdaVendors } from '../data';

export const TITLE = 'EDA and CW Vendors';

export const LandingPage = () => {
  const { getColor } = Theme.useIntelAccentColors();
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();
  const navigate = useNavigate();
  const { token } = Theme.useToken();
  const { user } = Auth.useUserAuth();

  const columns = useMemo(() => {
    const _columns: ColumnsType<AdvancedTechnologyEdaVendorDetail> = [
      {
        title: 'Supplier Id',
        dataIndex: ['supplier', 'externalId'],
        width: 200,
        ellipsis: true,
        responsive: ['lg'],
        sorter: true,
      },
      {
        title: 'Supplier Name',
        dataIndex: ['supplier', 'name'],
        ellipsis: true,
        sorter: true,
      },
      ...(isFeatureEnabled('EDA_VENDOR_TABLE_VIEW_PROCESS_POPOVER_FEATURE')
        ? [
            {
              title: 'Processes',
              dataIndex: 'advancedTechnologyEdaVendorProcesses',
              render: (
                _: unknown,
                {
                  advancedTechnologyEdaVendorProcesses,
                }: { advancedTechnologyEdaVendorProcesses: AdvancedTechnologyEdaVendorProcessDetail[] },
              ) => {
                return (
                  <Popover.Popover2Modal
                    placement="right"
                    title="Processes"
                    popoverHeight="auto"
                    width={500}
                    popoverWidth={300}
                    content={
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {advancedTechnologyEdaVendorProcesses
                          .sort((a, b) => a.process.name.localeCompare(b.process.name))
                          .map(({ process }) => {
                            return process ? <div>{process.name}</div> : undefined;
                          })}
                      </div>
                    }
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Button style={{}} type="link">
                        View Processes
                      </Button>
                    </div>
                  </Popover.Popover2Modal>
                );
              },
            },
          ]
        : [
            {
              title: 'Processes',
              dataIndex: 'advancedTechnologyEdaVendorProcesses',
              render: (
                _: unknown,
                {
                  advancedTechnologyEdaVendorProcesses,
                }: { advancedTechnologyEdaVendorProcesses: AdvancedTechnologyEdaVendorProcessDetail[] },
              ) => {
                return advancedTechnologyEdaVendorProcesses.map(({ process }) => {
                  const [bgColor, txtColor] = getColor(process.name);

                  return process ? (
                    <Tag key={process.id} style={{ color: txtColor }} color={bgColor}>
                      {process.name}
                    </Tag>
                  ) : undefined;
                });
              },
            },
          ]),
      {
        title: 'EDA Vendor',
        dataIndex: 'edaVendor',
        width: 110,
        filters: [
          { text: 'Is EDA Vendor', value: true },
          { text: 'Is Not EDA Vendor', value: false },
        ],
        filterMultiple: false,
        render: (value) => (value ? <CheckOutlined /> : ''),
      },
      {
        title: 'T3 Required',
        dataIndex: 't3Required',
        width: 110,
        filters: [
          { text: 'Required', value: true },
          { text: 'Not Required', value: false },
        ],
        filterMultiple: false,
        render: (value) => (value ? <CheckOutlined /> : ''),
      },
      UpdatedByColumn,
      UpdatedOnColumn,
      CreatedByColumn,
      CreatedOnColumn,
    ];

    if (user?.roles[Role.RULE_ADMIN])
      _columns.unshift({
        title: 'Action',
        width: 120,
        render: (_, record) => {
          return (
            <Space.Compact block>
              <Button
                size="small"
                icon={<EditOutlined style={{ color: token.colorPrimary }} />}
                onClick={() => navigate(`./${record.id}`)}
              />
              <Button
                size="small"
                icon={<DeleteOutlined style={{ color: token.red7 }} />}
                onClick={() => setDeleteTarget(record)}
              ></Button>
            </Space.Compact>
          );
        },
      });

    if (!isFeatureEnabled('vendor_check_required')) {
      return _columns.filter((x) => x.title !== 'T3 Required');
    }

    return _columns;
  }, [getColor, isFeatureEnabled, navigate, token.colorPrimary, token.red7, user?.roles]);

  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<AdvancedTechnologyEdaVendorDetail>('EDA and CW Vendor Mapping', columns, {
    customFilters: AuditFilters,
    globalSearchFields: ['supplier.name'],
  });

  const queryResult = useEdaVendors(getQuery());
  const del = useDeleteEdaVendor();
  const [deleteTarget, setDeleteTarget] = useState<AdvancedTechnologyEdaVendorDetail | undefined>(undefined);

  return (
    <>
      <ConfirmDelete.Modal
        itemId={deleteTarget?.id}
        itemName={deleteTarget?.supplier.name}
        onClose={() => setDeleteTarget(undefined)}
        onOk={del.mutate}
      />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: 'Admin' }, { title: 'Maps' }, { title: TITLE }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title={TITLE} />

        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} />,
              user?.roles[Role.RULE_ADMIN] ? <Button {...getCreateButtonProps()} /> : undefined,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink
                {...getCSVLinkProps(queryResult.data?.data, {
                  advancedTechnologyEdaVendorProcesses: (value: AdvancedTechnologyEdaVendorProcessDetail[]) =>
                    value.map(({ process }) => process.name).join(', '),
                })}
              />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
